/**
 * Storage providers — orchestrates local + Google Drive enable/disable + OAuth + folder bootstrap.
 */

import { useEffect, useRef, useState } from 'react';
import {
  folderUrl,
  getStorageRouter,
  getValidToken,
  loadStorageConfig,
  resetStorageRouter,
  saveStorageConfig,
  type StorageConfig,
  type SyncEvent,
} from '@skjv/storage';
import { AccountDivergenceModal } from './account-divergence-modal';
import { GoogleDriveRow } from './google-drive-row';
import { ProviderToggleRow } from './provider-toggle-row';
import { useGoogleDriveConnect } from './use-google-drive-connect';

const ENV_CLIENT_ID =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GOOGLE_OAUTH_CLIENT_ID) || '';

export function ProvidersSection() {
  const [config, setConfig] = useState<StorageConfig>(() => loadStorageConfig());
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [isSyncing, setSyncing] = useState(false);
  const [isSyncMessageFading, setSyncMessageFading] = useState(false);
  const [divergedEmail, setDivergedEmail] = useState<string | null>(null);
  const fadeStartTimer = useRef<number | null>(null);
  const fadeEndTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (fadeStartTimer.current !== null) window.clearTimeout(fadeStartTimer.current);
      if (fadeEndTimer.current !== null) window.clearTimeout(fadeEndTimer.current);
    };
  }, []);

  const clearFadeTimers = () => {
    if (fadeStartTimer.current !== null) {
      window.clearTimeout(fadeStartTimer.current);
      fadeStartTimer.current = null;
    }
    if (fadeEndTimer.current !== null) {
      window.clearTimeout(fadeEndTimer.current);
      fadeEndTimer.current = null;
    }
  };

  const applyConfig = (next: StorageConfig) => {
    saveStorageConfig(next);
    resetStorageRouter();
    setConfig(next);
  };

  const drive = useGoogleDriveConnect({ clientId: ENV_CLIENT_ID, config, applyConfig });

  const isGoogleConfigured = Boolean(ENV_CLIENT_ID);
  const driveCfg = config.providers['google-drive'];
  const currentFolderUrl = driveCfg.folderId ? folderUrl(driveCfg.folderId) : null;

  useEffect(() => {
    void refreshGoogleStatus(config, drive.setConnected);
  }, [config, drive.setConnected]);

  const handleToggleLocal = () => {
    applyConfig({
      ...config,
      providers: {
        ...config.providers,
        local: { enabled: !config.providers.local.enabled },
      },
    });
  };

  const handleSyncNow = async () => {
    clearFadeTimers();
    setSyncMessageFading(false);
    setSyncing(true);
    setSyncMessage('Checking Google identity…');
    try {
      const liveEmail = await drive.fetchLiveEmail();
      const savedEmail = driveCfg.email;
      if (savedEmail && liveEmail && savedEmail !== liveEmail) {
        setDivergedEmail(liveEmail);
        setSyncMessage(`Account mismatch (saved ${savedEmail}, active ${liveEmail}). Sync paused.`);
        setSyncing(false);
        return;
      }
    } catch {
      // Identity check is best-effort; fall through to folder + sync.
    }
    setSyncMessage('Validating Google Drive folder…');
    try {
      await drive.ensureRootFolderFresh();
    } catch (err) {
      setSyncMessage(`Folder validation failed: ${(err as Error).message}`);
      setSyncing(false);
      return;
    }
    setSyncMessage('Starting sync…');
    const router = getStorageRouter();
    const unsubscribe = router.onProgress((event) => {
      setSyncMessage(formatSyncEvent(event));
    });
    try {
      await router.forceRepair();
      fadeStartTimer.current = window.setTimeout(() => setSyncMessageFading(true), 2000);
      fadeEndTimer.current = window.setTimeout(() => {
        setSyncMessage(null);
        setSyncMessageFading(false);
      }, 2800);
    } catch (err) {
      setSyncMessage(`Sync failed: ${(err as Error).message}`);
    } finally {
      unsubscribe();
      setSyncing(false);
    }
  };

  return (
    <section className="border-t border-stone-200/50 pt-4 dark:border-stone-700/50">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-200">
          Storage Providers
        </h2>
        <button
          onClick={handleSyncNow}
          disabled={isSyncing}
          className="rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-100 disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-800"
        >
          {isSyncing ? 'Syncing…' : 'Sync now'}
        </button>
      </div>
      <p className="mb-2 text-xs text-stone-500 dark:text-stone-400">
        Your data is mirrored to every active provider. Reads merge the newest version and heal
        stale providers in the background.
      </p>
      {syncMessage && (
        <p
          className={`mb-3 text-xs text-stone-500 transition-opacity duration-700 dark:text-stone-400 ${
            isSyncMessageFading ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {syncMessage}
        </p>
      )}

      <ProviderToggleRow
        title="This device"
        description="Local browser storage. On by default; turn off to use cloud only."
        enabled={config.providers.local.enabled}
        onToggle={handleToggleLocal}
      />

      <GoogleDriveRow
        isConfigured={isGoogleConfigured}
        isConnected={drive.isConnected}
        isEnabled={driveCfg.enabled}
        isWorking={drive.isWorking}
        clientId={ENV_CLIENT_ID}
        folderUrl={currentFolderUrl}
        savedEmail={driveCfg.email ?? null}
        needsFolderCreation={drive.needsFolderCreation}
        statusMessage={drive.statusMessage}
        onConnect={drive.handleConnect}
        onCreateFolder={drive.handleCreateFolder}
        onDisconnect={drive.handleDisconnect}
      />

      {divergedEmail && driveCfg.email && (
        <AccountDivergenceModal
          savedEmail={driveCfg.email}
          liveEmail={divergedEmail}
          isWorking={drive.isWorking}
          onReconnect={async () => {
            setDivergedEmail(null);
            await drive.handleConnect();
          }}
          onDisconnect={async () => {
            setDivergedEmail(null);
            await drive.handleDisconnect();
          }}
          onDismiss={() => setDivergedEmail(null)}
        />
      )}
    </section>
  );
}

async function refreshGoogleStatus(
  config: StorageConfig,
  setConnected: (v: boolean) => void,
): Promise<void> {
  const gd = config.providers['google-drive'];
  if (!gd.enabled || !gd.clientId || !gd.folderId) {
    setConnected(false);
    return;
  }
  const token = await getValidToken(gd.clientId, gd.scope);
  setConnected(token !== null);
}

function formatSyncEvent(e: SyncEvent): string {
  switch (e.kind) {
    case 'sync-start':
      return `Starting sync — ${e.namespaces.length} buckets × ${e.providerIds.length} provider${
        e.providerIds.length === 1 ? '' : 's'
      }…`;
    case 'namespace-start':
      return `[${e.index + 1}/${e.total}] ${e.namespace}: listing…`;
    case 'listed':
      return `[${e.namespace}] ${e.providerId} has ${e.count} record${e.count === 1 ? '' : 's'}`;
    case 'merged':
      return `[${e.namespace}] merged → ${e.winners} winner${e.winners === 1 ? '' : 's'}`;
    case 'repair-plan': {
      const parts: string[] = [];
      if (e.writes) parts.push(`${e.writes} write${e.writes === 1 ? '' : 's'}`);
      if (e.tombstones) parts.push(`${e.tombstones} tombstone${e.tombstones === 1 ? '' : 's'}`);
      return `[${e.namespace}] repairing ${parts.join(' + ')}…`;
    }
    case 'repair-step':
      return `[${e.namespace}] ${e.action === 'tombstone' ? 'tombstoning' : 'writing'} ${
        e.key
      } → ${e.providerId}`;
    case 'namespace-done':
      return `[${e.namespace}] done (${e.durationMs}ms)`;
    case 'sync-done':
      return `Sync complete in ${(e.durationMs / 1000).toFixed(1)}s.`;
  }
}
